import {
  addCartItem,
  clearCartMood,
  createCart,
  createStoreOrderingSession,
  getCart,
  getAvailablePromotions,
  removeCartItem,
  setPromotions,
  setCartMood,
  updateCartItem,
} from '@nexacore/api-clients/fnb/public';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { responseErrorCode } from '@/features/entry/entryErrors';
import {
  earliestCartExpiry,
  isFutureCartExpiry,
  useCartWorkflowStore,
} from '@/state/cartWorkflowStore';
import { isFutureCapabilityExpiry, useCustomerSessionStore } from '@/state/customerSessionStore';
import type { PublicCart, PublicCartItem, PublicPromotionOption, SafeCartItemInput } from './types';

export const cartQueryKey = (session: string, store: string, cartId: string) =>
  ['public-cart', session, store, cartId] as const;

const promotionQueryKey = (cartId: string) => ['public-cart-promotions', cartId] as const;

function requireCart(response: Awaited<ReturnType<typeof createCart>>): PublicCart {
  const code = responseErrorCode(response);
  if (!response.succeeded || code || !response.data?.cart)
    throw Object.assign(new Error(code ?? 'FNB_PUBLIC_CART_INVALID'), { publicCode: code });
  return response.data.cart;
}

export function useCartQuery() {
  const session = useCustomerSessionStore((state) => state.orderingSession);
  const sessionExpiresAt = useCustomerSessionStore((state) => state.expiresAt);
  const store = useCustomerSessionStore((state) => state.store?.storeCode);
  const recovery = useCartWorkflowStore((state) => state.recovery);
  const canReadCart = Boolean(
    session &&
    isFutureCapabilityExpiry(sessionExpiresAt) &&
    recovery &&
    isFutureCartExpiry(recovery.expiresAt) &&
    (store ? store === recovery.storeCode : true)
  );
  return useQuery({
    queryKey: cartQueryKey(
      session ?? 'missing',
      store ?? recovery?.storeCode ?? 'missing',
      recovery?.cartId ?? 'missing'
    ),
    queryFn: async () => {
      if (!session || !recovery || !canReadCart)
        throw Object.assign(new Error('FNB_PUBLIC_CART_EXPIRED'), {
          publicCode: 'FNB_PUBLIC_CART_EXPIRED',
        });
      return requireCart(await getCart(recovery.cartId, session, recovery.cartProof));
    },
    enabled: canReadCart,
    retry: false,
    staleTime: 10_000,
  });
}

function recoveryFromCart(cart: PublicCart, storeCode: string) {
  if (
    !cart.id ||
    !cart.proofToken ||
    !cart.expiresAt ||
    !cart.recovery?.grant ||
    !cart.recovery.expiresAt ||
    !earliestCartExpiry(cart.expiresAt, cart.recovery.expiresAt)
  )
    throw new Error('FNB_PUBLIC_CART_INVALID');
  const expiresAt = earliestCartExpiry(cart.expiresAt, cart.recovery.expiresAt)!;
  return {
    cartId: cart.id,
    cartProof: cart.proofToken,
    recoveryGrant: cart.recovery.grant,
    expiresAt,
    storeCode,
  };
}

/** Only the server-issued recovery grant is persisted; Cart facts remain query/runtime data. */
function rotateRecoveryGrant(cart: PublicCart) {
  useCartWorkflowStore
    .getState()
    .rotateRecoveryGrant(cart.recovery?.grant, cart.recovery?.expiresAt, cart.expiresAt);
}

function refetchActiveCart(queryClient: QueryClient) {
  const session = useCustomerSessionStore.getState().orderingSession;
  const storeCode = useCustomerSessionStore.getState().store?.storeCode;
  const recovery = useCartWorkflowStore.getState().recovery;
  if (!session || !storeCode || !recovery) return;

  void queryClient
    .fetchQuery({
      queryKey: cartQueryKey(session, storeCode, recovery.cartId),
      queryFn: async () => requireCart(await getCart(recovery.cartId, session, recovery.cartProof)),
    })
    .catch(() => undefined);
}

export function useAddCartItem(storeCodeOverride?: string) {
  const queryClient = useQueryClient();
  const projectedStoreCode = useCustomerSessionStore((state) => state.store?.storeCode);
  const setSession = useCustomerSessionStore((state) => state.setSession);
  const storeCode = storeCodeOverride ?? projectedStoreCode;
  const setRecovery = useCartWorkflowStore((state) => state.setRecovery);
  return useMutation({
    mutationFn: async (input: SafeCartItemInput) => {
      if (!storeCode)
        throw Object.assign(new Error('FNB_PUBLIC_SESSION_INVALID'), {
          publicCode: 'FNB_PUBLIC_SESSION_INVALID',
        });

      const currentRecovery = useCartWorkflowStore.getState().recovery;
      if (currentRecovery && currentRecovery.storeCode !== storeCode)
        throw Object.assign(new Error('FNB_PUBLIC_CART_STORE_MISMATCH'), {
          publicCode: 'FNB_PUBLIC_CART_STORE_MISMATCH',
        });

      const currentSession = useCustomerSessionStore.getState();
      const currentSessionActive =
        Boolean(currentSession.orderingSession) &&
        Boolean(currentSession.expiresAt) &&
        isFutureCapabilityExpiry(currentSession.expiresAt) &&
        currentSession.store?.storeCode === storeCode;
      let activeSession = currentSession.orderingSession;

      if (!currentSessionActive) {
        const response = await createStoreOrderingSession({ storeCode, locale: 'vi-VN' });
        const code = responseErrorCode(response);
        const createdSession = response.data?.session;
        if (
          !response.succeeded ||
          code ||
          !createdSession?.token ||
          !isFutureCapabilityExpiry(createdSession.expiresAt) ||
          !createdSession.store
        )
          throw Object.assign(new Error(code ?? 'FNB_PUBLIC_SESSION_INVALID'), {
            publicCode: code ?? 'FNB_PUBLIC_SESSION_INVALID',
          });
        activeSession = createdSession.token;
        setSession(createdSession.token, createdSession.expiresAt, createdSession.store);
      }

      if (!activeSession)
        throw Object.assign(new Error('FNB_PUBLIC_SESSION_INVALID'), {
          publicCode: 'FNB_PUBLIC_SESSION_INVALID',
        });

      let active = currentRecovery;
      if (!active || active.storeCode !== storeCode || !isFutureCartExpiry(active.expiresAt)) {
        const mood = useCartWorkflowStore.getState().selectedMood;
        const created = requireCart(await createCart(activeSession, mood ?? {}));
        active = recoveryFromCart(created, storeCode);
        setRecovery(active);
        queryClient.setQueryData(cartQueryKey(activeSession, storeCode, active.cartId), created);
      }
      const current = queryClient.getQueryData<PublicCart>(
        cartQueryKey(activeSession, storeCode, active.cartId)
      );
      const cart = requireCart(
        await addCartItem(
          active.cartId,
          { ...input, expectedVersion: current?.version },
          activeSession,
          active.cartProof
        )
      );
      return { cart, active, activeSession, activeStoreCode: storeCode };
    },
    onSuccess: ({ cart, active, activeSession, activeStoreCode }) => {
      rotateRecoveryGrant(cart);
      queryClient.setQueryData(cartQueryKey(activeSession, activeStoreCode, active.cartId), cart);
    },
    onError: () => {
      // `recovery` can be null in this mutation closure when the cart was lazily
      // created immediately before an add failed. Read the current workflow state
      // so a price/availability/conflict response is reconciled from the server.
      const currentRecovery = useCartWorkflowStore.getState().recovery;
      const currentSession = useCustomerSessionStore.getState().orderingSession;
      if (currentSession && storeCode && currentRecovery)
        void queryClient.invalidateQueries({
          queryKey: cartQueryKey(currentSession, storeCode, currentRecovery.cartId),
        });
    },
  });
}

export function useSetCartMood() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (mood: { moodDefinitionId: string; requestedLocale: string }) => {
      const session = useCustomerSessionStore.getState().orderingSession;
      const storeCode = useCustomerSessionStore.getState().store?.storeCode;
      const recovery = useCartWorkflowStore.getState().recovery;
      if (!session || !storeCode || !recovery)
        return null;
      let current = queryClient.getQueryData<PublicCart>(
        cartQueryKey(session, storeCode, recovery.cartId)
      );
      if (!current?.version) {
        current = requireCart(await getCart(recovery.cartId, session, recovery.cartProof));
        queryClient.setQueryData(cartQueryKey(session, storeCode, recovery.cartId), current);
      }
      const response = await setCartMood(
        recovery.cartId,
        { ...mood, expectedVersion: current?.version },
        session,
        recovery.cartProof
      );
      return requireCart(response);
    },
    onSuccess: (cart) => {
      if (!cart) return;
      rotateRecoveryGrant(cart);
      const session = useCustomerSessionStore.getState().orderingSession;
      const storeCode = useCustomerSessionStore.getState().store?.storeCode;
      const recovery = useCartWorkflowStore.getState().recovery;
      if (session && storeCode && recovery)
        queryClient.setQueryData(cartQueryKey(session, storeCode, recovery.cartId), cart);
    },
    onError: () => refetchActiveCart(queryClient),
  });
}

export function useClearCartMood() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const session = useCustomerSessionStore.getState().orderingSession;
      const storeCode = useCustomerSessionStore.getState().store?.storeCode;
      const recovery = useCartWorkflowStore.getState().recovery;
      if (!session || !storeCode || !recovery)
        throw new Error('FNB_PUBLIC_CART_ACCESS_DENIED');
      let current = queryClient.getQueryData<PublicCart>(
        cartQueryKey(session, storeCode, recovery.cartId)
      );
      if (!current?.version) {
        current = requireCart(await getCart(recovery.cartId, session, recovery.cartProof));
        queryClient.setQueryData(cartQueryKey(session, storeCode, recovery.cartId), current);
      }
      return requireCart(
        await clearCartMood(
          recovery.cartId,
          current?.version ?? 0,
          session,
          recovery.cartProof
        )
      );
    },
    onSuccess: (cart) => {
      rotateRecoveryGrant(cart);
      useCartWorkflowStore.getState().setSelectedMood(null);
      const session = useCustomerSessionStore.getState().orderingSession;
      const storeCode = useCustomerSessionStore.getState().store?.storeCode;
      const recovery = useCartWorkflowStore.getState().recovery;
      if (session && storeCode && recovery)
        queryClient.setQueryData(cartQueryKey(session, storeCode, recovery.cartId), cart);
    },
    onError: () => refetchActiveCart(queryClient),
  });
}

export function useUpdateCartItem() {
  const queryClient = useQueryClient();
  const session = useCustomerSessionStore((state) => state.orderingSession)!;
  const storeCode = useCustomerSessionStore((state) => state.store?.storeCode);
  const recovery = useCartWorkflowStore((state) => state.recovery);
  return useMutation({
    mutationFn: async ({
      item,
      changes,
    }: {
      item: PublicCartItem;
      changes: Partial<
        Pick<SafeCartItemInput, 'variantId' | 'modifierOptionIds' | 'quantity' | 'itemNote'>
      >;
    }) => {
      if (!session || !storeCode || !recovery || !item.id || !item.productId)
        throw Object.assign(new Error('FNB_PUBLIC_CART_ACCESS_DENIED'), {
          publicCode: 'FNB_PUBLIC_CART_ACCESS_DENIED',
        });
      const current = queryClient.getQueryData<PublicCart>(
        cartQueryKey(session, storeCode, recovery.cartId)
      );
      const input: SafeCartItemInput & { expectedVersion?: number } = {
        productId: item.productId,
        variantId: changes.variantId ?? item.variantId,
        modifierOptionIds: changes.modifierOptionIds ?? item.modifierOptionIds ?? [],
        quantity: changes.quantity ?? item.quantity,
        itemNote: changes.itemNote ?? item.itemNote,
        expectedVersion: current?.version,
      };
      return requireCart(
        await updateCartItem(recovery.cartId, item.id, input, session, recovery.cartProof)
      );
    },
    onSuccess: (cart) => {
      rotateRecoveryGrant(cart);
      if (recovery && storeCode)
        queryClient.setQueryData(cartQueryKey(session, storeCode, recovery.cartId), cart);
    },
    onError: () =>
      recovery &&
      storeCode &&
      void queryClient.invalidateQueries({
        queryKey: cartQueryKey(session, storeCode, recovery.cartId),
      }),
  });
}

export function useRemoveCartItem() {
  const queryClient = useQueryClient();
  const session = useCustomerSessionStore((state) => state.orderingSession)!;
  const storeCode = useCustomerSessionStore((state) => state.store?.storeCode);
  const recovery = useCartWorkflowStore((state) => state.recovery);
  return useMutation({
    mutationFn: async (itemId: string) => {
      if (!session || !storeCode || !recovery)
        throw Object.assign(new Error('FNB_PUBLIC_CART_ACCESS_DENIED'), {
          publicCode: 'FNB_PUBLIC_CART_ACCESS_DENIED',
        });
      const current = queryClient.getQueryData<PublicCart>(
        cartQueryKey(session, storeCode, recovery.cartId)
      );
      return requireCart(
        await removeCartItem(
          recovery.cartId,
          itemId,
          current?.version ?? 0,
          session,
          recovery.cartProof
        )
      );
    },
    onSuccess: (cart) => {
      rotateRecoveryGrant(cart);
      if (recovery && storeCode)
        queryClient.setQueryData(cartQueryKey(session, storeCode, recovery.cartId), cart);
    },
    onError: () =>
      recovery &&
      storeCode &&
      void queryClient.invalidateQueries({
        queryKey: cartQueryKey(session, storeCode, recovery.cartId),
      }),
  });
}

export function useAvailablePromotions(open: boolean) {
  const session = useCustomerSessionStore((state) => state.orderingSession);
  const sessionExpiresAt = useCustomerSessionStore((state) => state.expiresAt);
  const recovery = useCartWorkflowStore((state) => state.recovery);
  const canReadPromotions = Boolean(
    session &&
    isFutureCapabilityExpiry(sessionExpiresAt) &&
    recovery &&
    isFutureCartExpiry(recovery.expiresAt)
  );
  return useQuery({
    queryKey: promotionQueryKey(recovery?.cartId ?? 'missing'),
    queryFn: async (): Promise<PublicPromotionOption[]> => {
      const response = await getAvailablePromotions(
        recovery!.cartId,
        session!,
        recovery!.cartProof
      );
      const code = responseErrorCode(response);
      if (!response.succeeded || code)
        throw Object.assign(new Error(code ?? 'FNB_PUBLIC_PROMOTION_INVALID'), {
          publicCode: code,
        });
      return response.data?.promotions ?? [];
    },
    enabled: open && canReadPromotions,
    staleTime: 10_000,
    retry: false,
  });
}

export function useSetPromotions() {
  const queryClient = useQueryClient();
  const session = useCustomerSessionStore((state) => state.orderingSession);
  const storeCode = useCustomerSessionStore((state) => state.store?.storeCode);
  const recovery = useCartWorkflowStore((state) => state.recovery);
  return useMutation({
    mutationFn: async (codes: string[]) => {
      if (!session || !storeCode || !recovery)
        throw Object.assign(new Error('FNB_PUBLIC_CART_ACCESS_DENIED'), {
          publicCode: 'FNB_PUBLIC_CART_ACCESS_DENIED',
        });
      const current = queryClient.getQueryData<PublicCart>(
        cartQueryKey(session, storeCode, recovery.cartId)
      );
      const response = await setPromotions(
        recovery.cartId,
        { codes, expectedVersion: current?.version },
        session,
        recovery.cartProof
      );
      const code = responseErrorCode(response);
      if (!response.succeeded || code || !response.data?.cart)
        throw Object.assign(new Error(code ?? 'FNB_PUBLIC_PROMOTION_INVALID'), {
          publicCode: code,
        });
      return {
        cart: response.data.cart as PublicCart,
        promotions: response.data.promotions ?? [],
      };
    },
    onSuccess: ({ cart, promotions }) => {
      rotateRecoveryGrant(cart);
      if (session && storeCode && recovery) {
        queryClient.setQueryData(cartQueryKey(session, storeCode, recovery.cartId), cart);
        queryClient.setQueryData(promotionQueryKey(recovery.cartId), promotions);
      }
    },
    onError: () => {
      if (session && storeCode && recovery)
        void queryClient.invalidateQueries({
          queryKey: cartQueryKey(session, storeCode, recovery.cartId),
        });
    },
  });
}
